import { useCallback, useEffect, useRef, useState } from "react";
import { type ConnectionState, APIClient } from "./api";
import { AudioFeedback } from "./audio";
import {
  type CombatMode,
  type CombatViewState,
  clearedCombatState,
  normalizeRunView,
  raceCombatState,
  restoredCombatState,
  roomCombatState,
  singleCombatState,
} from "./combatState";
import {
  type EffectChannel,
  type EffectSettings,
  applyEffectSettings,
  readStoredEffectSettings,
} from "./effects";
import {
  type ThemeId,
  applyFontScale,
  applyTheme,
  FONT_SCALE_MAX,
  FONT_SCALE_MIN,
  FONT_SCALE_STEP,
  readStoredFontScale,
  readStoredTheme,
} from "./theme";
import {
  type AutoSellQuality,
  type ConvertibleResource,
  type GrowthProfile,
  type InventoryView,
  type LifeProfile,
  type MeResponse,
  type RaceView,
  type RedeemResult,
  type ResourceConversionResult,
  type RoomView,
  type RunView,
  type Session,
  type SocialProfile,
  type TaskBoard,
  APIError,
} from "./types";
import { raidCueRate } from "./ui/EnemyDetails";
import { messageOf } from "./ui/format";
import { type AppStage, type AuthMode, type HubTab } from "./ui/navigation";
import { useUIFeedback } from "./uiFeedback";
import { type SimWasmStatus, loadSimWasm } from "./wasm";

export function useAppController() {
  useUIFeedback();
  const [session, setSession] = useState<Session | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>("disconnected");
  const [browserOnline, setBrowserOnline] = useState(
    () => typeof navigator === "undefined" || navigator.onLine,
  );
  const connected = connectionState === "connected" && browserOnline;
  const apiRef = useRef<APIClient | null>(null);
  if (!apiRef.current) apiRef.current = new APIClient(setSession);
  const api = apiRef.current;
  const [me, setMe] = useState<MeResponse | null>(null);
  const [inventory, setInventory] = useState<InventoryView | null>(null);
  const [growth, setGrowth] = useState<GrowthProfile | null>(null);
  const [lifeProfile, setLifeProfile] = useState<LifeProfile | null>(null);
  const [lifeEnhancementItemId, setLifeEnhancementItemId] = useState("");
  const [hubTab, setHubTab] = useState<HubTab>("equipment");
  const [view, setView] = useState<RunView | null>(null);
  const [roomView, setRoomView] = useState<RoomView | null>(null);
  const [raceView, setRaceView] = useState<RaceView | null>(null);
  const combatModeRef = useRef<CombatMode>("idle");
  const [social, setSocial] = useState<SocialProfile | null>(null);
  const [tasks, setTasks] = useState<TaskBoard | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [runQueued, setRunQueued] = useState(false);
  const [runEnding, setRunEnding] = useState(false);
  const runEndingRef = useRef(false);
  const queuedRunAction = useRef<(() => Promise<RunView>) | null>(null);
  const [error, setError] = useState("");
  const [authMode, setAuthMode] = useState<AuthMode | null>(null);
  const [adminOpen, setAdminOpen] = useState(false);
  const [offlineRewardsOpen, setOfflineRewardsOpen] = useState(false);
  const offlineRewards = me?.player.offline?.rewards;
  const offlineRewardKey =
    me && offlineRewards ? `breacher.offline-reward:${me.user.id}:${offlineRewards.id}` : "";
  useEffect(() => {
    if (!offlineRewardKey || !offlineRewards || offlineRewards.seconds <= 0) {
      setOfflineRewardsOpen(false);
      return;
    }
    try {
      setOfflineRewardsOpen(localStorage.getItem(offlineRewardKey) !== "seen");
    } catch {
      setOfflineRewardsOpen(true);
    }
  }, [offlineRewardKey, offlineRewards?.seconds]);
  const dismissOfflineRewards = () => {
    setOfflineRewardsOpen(false);
    try {
      if (offlineRewardKey) localStorage.setItem(offlineRewardKey, "seen");
    } catch {
      /* Read-only browser storage must not block play. */
    }
  };
  const [colorBlind, setColorBlind] = useState(
    () => localStorage.getItem("breacher.colorblind") === "1",
  );
  const [muted, setMuted] = useState(() => localStorage.getItem("breacher.muted") === "1");
  const [theme, setTheme] = useState<ThemeId>(readStoredTheme);
  const [fontScale, setFontScale] = useState(readStoredFontScale);
  const [effectSettings, setEffectSettings] = useState<EffectSettings>(readStoredEffectSettings);
  const [simStatus, setSimStatus] = useState<SimWasmStatus>({
    status: "unavailable",
    reason: "not-loaded",
  });
  const audioRef = useRef(new AudioFeedback());
  const lastFeedbackRevision = useRef(-1);
  const lastRaidCue = useRef({ id: "", attackCount: 0 });
  const lastRoomFeedback = useRef({ roomId: "", boardHash: "" });
  const playerRefreshSequence = useRef(0);
  const lifeProjectionSequence = useRef(0);

  const applyCombatState = useCallback((state: CombatViewState) => {
    combatModeRef.current = state.mode;
    setView(state.view);
    setRoomView(state.roomView);
    setRaceView(state.raceView);
  }, []);

  useEffect(() => {
    void loadSimWasm().then(setSimStatus);
  }, []);

  useEffect(() => audioRef.current.setMuted(muted), [muted]);

  useEffect(() => applyTheme(theme), [theme]);

  useEffect(() => applyFontScale(fontScale), [fontScale]);

  useEffect(() => applyEffectSettings(effectSettings), [effectSettings]);

  useEffect(() => {
    const unlock = () => {
      void audioRef.current.unlock();
    };
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      audioRef.current.dispose();
    };
  }, []);

  useEffect(() => {
    if (!view || view.run.revision === lastFeedbackRevision.current) return;
    lastFeedbackRevision.current = view.run.revision;
    if (view.run.lastDamage) audioRef.current.play("hit");
    else if (view.run.status === "ended") audioRef.current.play("reward");
  }, [view]);

  useEffect(() => {
    const boss = roomView?.room.status === "active" ? roomView.room.boss : undefined;
    if (!boss) {
      lastRaidCue.current = { id: "", attackCount: 0 };
      return;
    }
    const rate = raidCueRate(boss.trait);
    if (lastRaidCue.current.id !== boss.id) {
      lastRaidCue.current = { id: boss.id, attackCount: boss.attackCount };
      audioRef.current.play("skill", { rate, volume: 0.22 });
      return;
    }
    if (boss.attackCount > lastRaidCue.current.attackCount) {
      audioRef.current.play("attack", { rate, volume: 0.3 });
      if (boss.trait === "vein_mark" && boss.attackCount % 2 === 0)
        audioRef.current.play("hit", { delay: 0.12, rate: 1.35, volume: 0.3 });
      if (boss.trait === "drain_ritual")
        audioRef.current.play("skill", { delay: 0.16, rate: 0.72, volume: 0.2 });
    }
    lastRaidCue.current = { id: boss.id, attackCount: boss.attackCount };
  }, [roomView]);

  useEffect(() => {
    const room = roomView?.room;
    const participant = room?.participants.find((current) => current.userId === roomView?.you);
    if (!room || room.status !== "active" || !participant) {
      lastRoomFeedback.current = { roomId: "", boardHash: "" };
      return;
    }
    if (lastRoomFeedback.current.roomId !== room.id) {
      lastRoomFeedback.current = { roomId: room.id, boardHash: participant.boardHash };
      return;
    }
    if (lastRoomFeedback.current.boardHash === participant.boardHash) return;
    lastRoomFeedback.current.boardHash = participant.boardHash;
    const cascades = participant.lastCascades ?? [];
    if (cascades.length > 0) {
      audioRef.current.play("match");
      if (cascades.some((cascade) => cascade.groups?.some((group) => group.tile === 0)))
        audioRef.current.play("attack", { delay: 0.04 });
      if (participant.lastDamage) audioRef.current.play("hit", { delay: 0.1 });
      audioRef.current.play("drop", { delay: 0.17 });
      const chainTier = Math.max(...cascades.map((cascade) => cascade.layer));
      if (
        chainTier > 1 ||
        cascades.some((cascade) => cascade.groups?.some((group) => group.count >= 4))
      )
        audioRef.current.play("combo", { delay: 0.06, tier: chainTier });
    } else if (participant.lastDamage) {
      audioRef.current.play("hit");
    }
  }, [roomView]);

  const updateView = useCallback((next: RunView) => {
    if (combatModeRef.current !== "single") return;
    const normalized = normalizeRunView(next);
    setRoomView(null);
    setRaceView(null);
    setView((current) =>
      !current || normalized.run.revision >= current.run.revision ? normalized : current,
    );
  }, []);

  const updateRoomView = useCallback((next: RoomView) => {
    if (combatModeRef.current !== "room") return;
    setView(null);
    setRaceView(null);
    setRoomView(next);
  }, []);

  const updateRaceView = useCallback((next: RaceView) => {
    if (combatModeRef.current !== "race") return;
    setView(null);
    setRoomView(null);
    setRaceView(next);
  }, []);

  const refreshPlayerState = useCallback(
    async (
      overrides: {
        me?: MeResponse;
        inventory?: InventoryView;
        growth?: GrowthProfile;
        tasks?: TaskBoard;
        life?: LifeProfile;
      } = {},
    ) => {
      const sequence = ++playerRefreshSequence.current;
      ++lifeProjectionSequence.current;
      if (overrides.me) setMe(overrides.me);
      if (overrides.inventory) setInventory(overrides.inventory);
      if (overrides.growth) setGrowth(overrides.growth);
      if (overrides.tasks) setTasks(overrides.tasks);
      if (overrides.life) setLifeProfile(overrides.life);
      const [nextMe, nextInventory, nextGrowth, nextTasks, nextLife] = await Promise.all([
        api.me(),
        api.inventory(),
        api.growth(),
        api.tasks(),
        api.life(),
      ]);
      if (sequence !== playerRefreshSequence.current) return;
      setMe(overrides.me ?? nextMe);
      setInventory(overrides.inventory ?? nextInventory);
      setGrowth(overrides.growth ?? nextGrowth);
      setTasks(overrides.tasks ?? nextTasks);
      setLifeProfile(overrides.life ?? nextLife);
    },
    [api],
  );

  const loadPlayer = refreshPlayerState;

  const refreshLifeProjection = useCallback(async () => {
    const sequence = ++lifeProjectionSequence.current;
    const next = await api.life();
    if (sequence === lifeProjectionSequence.current) setLifeProfile(next);
  }, [api]);

  const loadSessionState = useCallback(async () => {
    const [nextMe, nextInventory, nextGrowth, nextTasks, nextLife, current] = await Promise.all([
      api.me(),
      api.inventory(),
      api.growth(),
      api.tasks(),
      api.life(),
      api.currentRun().catch((requestError) => {
        if (requestError instanceof APIError && requestError.code === "no_active_run") return null;
        throw requestError;
      }),
    ]);
    const [currentRoom, currentRace, nextSocial] = await Promise.all([
      api.currentRoom().catch(() => null),
      api.currentRace().catch(() => null),
      api.social().catch(() => null),
    ]);
    setMe(nextMe);
    setInventory(nextInventory);
    setGrowth(nextGrowth);
    setTasks(nextTasks);
    setLifeProfile(nextLife);
    applyCombatState(restoredCombatState(current, currentRoom, currentRace));
    setSocial(nextSocial);
  }, [api, applyCombatState]);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const restored = await api.bootstrap();
        if (!active) return;
        if (restored) await loadSessionState();
      } catch (requestError) {
        if (active) setError(messageOf(requestError));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [api, loadSessionState]);

  useEffect(() => api.onRunState(updateView), [api, updateView]);
  useEffect(() => api.onRoomState(updateRoomView), [api, updateRoomView]);
  useEffect(() => api.onRaceState(updateRaceView), [api, updateRaceView]);
  useEffect(() => api.onConnection(setConnectionState), [api]);
  useEffect(() => {
    const update = () => setBrowserOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);
  useEffect(() => {
    if (
      roomView?.room.status === "results" ||
      roomView?.room.status === "ended" ||
      (raceView && raceView.race.status !== "active")
    )
      void loadPlayer();
  }, [loadPlayer, raceView, roomView]);

  useEffect(() => {
    if (!connected || !tasks) return;
    const nextResetAt = Math.min(tasks.dailyResetAt, tasks.weeklyResetAt);
    const delay = Math.max(1_000, nextResetAt - tasks.serverNow + 250);
    const timer = window.setTimeout(() => {
      void api
        .tasks()
        .then(setTasks)
        .catch(() => undefined);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [api, connected, tasks?.dailyResetAt, tasks?.weeklyResetAt]);

  const act = useCallback(
    async (operation: () => Promise<RunView>, priority: "normal" | "retreat" = "normal") => {
      if (!connected) return;
      if (priority === "retreat") {
        runEndingRef.current = true;
        setRunEnding(true);
        queuedRunAction.current = null;
        setRunQueued(false);
      }
      if (busyRef.current) {
        if (priority === "retreat" || (!runEndingRef.current && !queuedRunAction.current)) {
          queuedRunAction.current = operation;
          setRunQueued(true);
        }
        return;
      }
      busyRef.current = true;
      setBusy(true);
      setError("");
      try {
        let current: (() => Promise<RunView>) | null = operation;
        while (current) {
          let next: RunView;
          try {
            next = await current();
          } catch (requestError) {
            if (runEndingRef.current && queuedRunAction.current) {
              current = queuedRunAction.current;
              queuedRunAction.current = null;
              setRunQueued(false);
              continue;
            }
            throw requestError;
          }
          lastFeedbackRevision.current = next.run.revision;
          const cascades = next.run.lastCascades ?? [];
          const powerMatch = cascades.some((cascade) =>
            cascade.groups?.some((group) => group.count >= 4),
          );
          if (next.run.status === "ended") {
            audioRef.current.play("reward");
          } else if (cascades.length > 0) {
            audioRef.current.play("match");
            if (cascades.some((cascade) => cascade.groups?.some((group) => group.tile === 0)))
              audioRef.current.play("attack", { delay: 0.04 });
            if (next.run.lastDamage) audioRef.current.play("hit", { delay: 0.1 });
            audioRef.current.play("drop", { delay: 0.17 });
            const chainTier = Math.max(...cascades.map((cascade) => cascade.layer));
            if (powerMatch || chainTier > 1)
              audioRef.current.play("combo", { delay: 0.06, tier: chainTier });
          } else if (next.run.lastDamage) {
            audioRef.current.play("hit");
          } else {
            audioRef.current.play("slide");
          }
          updateView(next);
          if (next.run.status === "ended") {
            queuedRunAction.current = null;
            setRunQueued(false);
            break;
          }
          current = queuedRunAction.current;
          queuedRunAction.current = null;
          setRunQueued(false);
        }
      } catch (requestError) {
        queuedRunAction.current = null;
        setRunQueued(false);
        const apiError = requestError instanceof APIError ? requestError : null;
        if (apiError?.code !== "run_busy") setError(messageOf(requestError));
      } finally {
        runEndingRef.current = false;
        setRunEnding(false);
        busyRef.current = false;
        setBusy(false);
      }
    },
    [connected, updateView],
  );

  const startRun = async (shaftDepth: number, useFreeSkip: boolean) => {
    applyCombatState(singleCombatState());
    await act(() => api.startRun(shaftDepth, useFreeSkip));
  };

  const enterRoom = async (operation: () => Promise<RoomView>) => {
    applyCombatState(roomCombatState());
    try {
      updateRoomView(await operation());
    } catch (requestError) {
      applyCombatState(clearedCombatState());
      throw requestError;
    }
  };

  const enterRace = async (operation: () => Promise<RaceView>) => {
    applyCombatState(raceCombatState());
    try {
      updateRaceView(await operation());
    } catch (requestError) {
      applyCombatState(clearedCombatState());
      throw requestError;
    }
  };

  const leaveMultiplayer = async () => {
    await api.leaveRoom();
    applyCombatState(clearedCombatState());
    void loadPlayer();
  };

  const purchaseDiamondHeal = async () => {
    if (busy || !connected) return;
    setBusy(true);
    setError("");
    try {
      await api.purchaseDiamondHeal();
      setMe(await api.me());
      audioRef.current.play("reward");
    } catch (requestError) {
      setError(messageOf(requestError));
    } finally {
      setBusy(false);
    }
  };

  const returnToHub = async () => {
    applyCombatState(clearedCombatState());
    setHubTab("equipment");
    await loadPlayer().catch((requestError) => setError(messageOf(requestError)));
  };

  const acknowledgeRoomSettlement = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      updateRoomView(await api.acknowledgeRoomResult());
      await loadPlayer();
    } catch (requestError) {
      setError(messageOf(requestError));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    await api.logout();
    applyCombatState(clearedCombatState());
    setMe(null);
    setInventory(null);
    setGrowth(null);
    setLifeProfile(null);
    setTasks(null);
    setSocial(null);
    setHubTab("equipment");
    setAdminOpen(false);
    setAuthMode(null);
    setLoading(false);
  };

  const enterSession = async (operation: () => Promise<Session>) => {
    setError("");
    await operation();
    try {
      await loadSessionState();
      setHubTab("equipment");
    } catch (requestError) {
      setError(messageOf(requestError));
      throw requestError;
    }
  };

  const retryOffline = async () => {
    if (!browserOnline || connectionState === "retrying") return;
    setError("");
    try {
      await api.retryConnection();
      if (!me) await loadSessionState();
    } catch (requestError) {
      setError(messageOf(requestError));
    }
  };

  const toggleColorBlind = () => {
    setColorBlind((current) => {
      const next = !current;
      localStorage.setItem("breacher.colorblind", next ? "1" : "0");
      return next;
    });
  };

  const toggleMuted = () => {
    setMuted((current) => {
      const next = !current;
      localStorage.setItem("breacher.muted", next ? "1" : "0");
      return next;
    });
  };

  const changeFontScale = (value: number) => {
    if (!Number.isFinite(value)) return;
    setFontScale(
      Math.min(
        FONT_SCALE_MAX,
        Math.max(FONT_SCALE_MIN, Math.round(value / FONT_SCALE_STEP) * FONT_SCALE_STEP),
      ),
    );
  };

  const changeEffectChannel = (
    channel: EffectChannel,
    patch: Partial<EffectSettings[EffectChannel]>,
  ) => {
    setEffectSettings((current) => ({ ...current, [channel]: { ...current[channel], ...patch } }));
  };

  const changeAutoSellQuality = async (quality: AutoSellQuality) => {
    if (busy || !connected) return;
    setBusy(true);
    setError("");
    try {
      const settings = await api.updatePlayerSettings(quality);
      setMe((current) =>
        current
          ? { ...current, player: { ...current.player, autoSellQuality: settings.autoSellQuality } }
          : current,
      );
    } catch (requestError) {
      setError(messageOf(requestError));
    } finally {
      setBusy(false);
    }
  };

  const changeOffline = async (operation: () => Promise<unknown>) => {
    if (busy || !connected) return;
    setBusy(true);
    setError("");
    try {
      await operation();
      await refreshPlayerState();
      audioRef.current.play("reward");
    } catch (requestError) {
      setError(messageOf(requestError));
    } finally {
      setBusy(false);
    }
  };

  const changeEquipment = async (operation: () => Promise<InventoryView>) => {
    if (busy || !connected) return;
    setBusy(true);
    setError("");
    try {
      const next = await operation();
      await refreshPlayerState({ inventory: next });
    } catch (requestError) {
      setError(messageOf(requestError));
    } finally {
      setBusy(false);
    }
  };

  const changeGrowth = async (operation: () => Promise<GrowthProfile>) => {
    if (busy || !connected) return;
    setBusy(true);
    setError("");
    try {
      const nextGrowth = await operation();
      await refreshPlayerState({ growth: nextGrowth });
    } catch (requestError) {
      setError(messageOf(requestError));
    } finally {
      setBusy(false);
    }
  };

  const changeLife = async (operation: () => Promise<LifeProfile>) => {
    if (busy || !connected) return;
    ++lifeProjectionSequence.current;
    setBusy(true);
    setError("");
    try {
      const nextLife = await operation();
      await refreshPlayerState({ life: nextLife });
    } catch (requestError) {
      setError(messageOf(requestError));
    } finally {
      setBusy(false);
    }
  };

  const convertWorkshopResources = async (
    sourceCurrency: ConvertibleResource,
    targetCurrency: ConvertibleResource,
    sourceAmount: number,
  ): Promise<ResourceConversionResult> => {
    if (busy || !connected) throw new APIError("connection_unavailable", "连接尚未完成", 503);
    setBusy(true);
    setError("");
    try {
      const result = await api.convertWorkshopResources(
        sourceCurrency,
        targetCurrency,
        sourceAmount,
      );
      await refreshPlayerState({ growth: result.profile });
      audioRef.current.play("reward");
      return result;
    } finally {
      setBusy(false);
    }
  };

  const redeemCode = async (code: string): Promise<RedeemResult> => {
    if (busy || !connected) throw new APIError("connection_unavailable", "连接尚未完成", 503);
    setBusy(true);
    setError("");
    try {
      const result = await api.redeemCode(code);
      await loadPlayer();
      audioRef.current.play("reward");
      return result;
    } finally {
      setBusy(false);
    }
  };

  const claimTask = async (taskId: string) => {
    if (busy || !connected) return;
    setBusy(true);
    setError("");
    try {
      const nextTasks = await api.claimTask(taskId);
      await refreshPlayerState({ tasks: nextTasks });
      audioRef.current.play("reward");
    } catch (requestError) {
      setError(messageOf(requestError));
    } finally {
      setBusy(false);
    }
  };

  const claimAllTasks = async () => {
    if (busy || !connected || !tasks) return;
    const claimable = tasks.tasks.filter((task) => task.claimable);
    if (claimable.length === 0) return;
    setBusy(true);
    setError("");
    try {
      let nextTasks = tasks;
      for (const task of claimable) nextTasks = await api.claimTask(task.id);
      await refreshPlayerState({ tasks: nextTasks });
      audioRef.current.play("reward");
    } catch (requestError) {
      await refreshPlayerState().catch(() => undefined);
      setError(messageOf(requestError));
    } finally {
      setBusy(false);
    }
  };

  const changeSocial = async (
    operation: () => Promise<SocialProfile>,
    refreshResources = false,
  ) => {
    if (busy || !connected) return;
    setBusy(true);
    setError("");
    try {
      setSocial(await operation());
      if (refreshResources) await refreshPlayerState();
    } catch (requestError) {
      setError(messageOf(requestError));
    } finally {
      setBusy(false);
    }
  };

  const stage: AppStage =
    connectionState === "offline" || connectionState === "retrying"
      ? "offline"
      : loading || (session && !me && !error)
        ? "booting"
        : !session
          ? "auth"
          : roomView?.room.status === "results"
            ? "room-settlement"
            : roomView && roomView.room.status !== "ended"
              ? "room"
              : raceView?.race.status === "active"
                ? "race"
                : view?.run.status === "active"
                  ? "game"
                  : view?.run.status === "ended"
                    ? "settlement"
                    : me
                      ? "hub"
                      : "fatal";
  return {
    stage,
    error,
    simStatus,
    connected,
    muted,
    toggleMuted,
    hubTab,
    setHubTab,
    connectionState,
    browserOnline,
    retryOffline,
    offlineRewardsOpen,
    offlineRewards,
    dismissOfflineRewards,
    setError,
    loading,
    enterSession,
    api,
    me,
    inventory,
    growth,
    lifeProfile,
    refreshPlayerState,
    social,
    tasks,
    busy,
    startRun,
    purchaseDiamondHeal,
    changeOffline,
    enterRoom,
    enterRace,
    changeEquipment,
    setLifeEnhancementItemId,
    changeGrowth,
    convertWorkshopResources,
    changeLife,
    refreshLifeProjection,
    lifeEnhancementItemId,
    redeemCode,
    claimTask,
    claimAllTasks,
    changeSocial,
    theme,
    fontScale,
    effectSettings,
    colorBlind,
    setTheme,
    toggleColorBlind,
    changeFontScale,
    changeEffectChannel,
    changeAutoSellQuality,
    setAuthMode,
    session,
    setAdminOpen,
    view,
    runEnding,
    runQueued,
    act,
    audioRef,
    roomView,
    updateRoomView,
    leaveMultiplayer,
    raceView,
    updateRaceView,
    applyCombatState,
    loadPlayer,
    returnToHub,
    acknowledgeRoomSettlement,
    adminOpen,
    authMode,
    setMe,
    setInventory,
    setGrowth,
    setLifeProfile,
    setTasks,
    setSocial,
    logout,
  };
}
